import axios, { AxiosRequestConfig } from 'axios/index'
import { getAppConfig } from '../config/appConfig'
import { getApiKey } from '../config/apiKey'
import Cookies from 'js-cookie'
import { setChallengeAuthenticationAction } from '../store/slices/authSlice'

import axiosRetry from 'axios-retry'
import { analyticsService } from '../services/analytics-service'
import { Dispatch } from '@reduxjs/toolkit'

const AxiosUtil = axios.create()

const initAxios = () => {
  AxiosUtil.defaults.baseURL = getAppConfig().REACT_APP_SERVICE_URL
}

axiosRetry(AxiosUtil, { retries: 0 })
let requestInterceptorId = 0
let responseInterceptorId = 0
let angularRequestInterceptorId = 0
let angularResponseInterceptorId = 0

interface ConfigWithId extends AxiosRequestConfig {
  id: number
}

const addAxiosInterceptors = (dispatch: Dispatch) => {
  //If interceptors already exist, eject them to avoid duplication of interceptors
  AxiosUtil.interceptors.request.eject(requestInterceptorId)
  AxiosUtil.interceptors.response.eject(responseInterceptorId)
  requestInterceptorId = AxiosUtil.interceptors.request.use(
    config => {
      const accessToken = localStorage.getItem('access_token')
      const idToken = localStorage.getItem('id_token')

      if (isSSOSessionActive()) {
        // add auth headers only if sso session is alive
        if (accessToken && config.headers) {
          config.headers.Authorization = accessToken
        }
        if (idToken && config.headers) {
          config.headers.id_token = idToken
        }
      }
      config.params = { key: getApiKey() }
      try {
        ;(config as ConfigWithId).id = analyticsService.requestTimerStart(
          config.url!,
        )
      } catch (error) {
        console.error('Error while tracking analytics call', error)
      }
      return config
    },
    error => {
      analyticsService.axiosException(error)
      return error
    },
  )

  responseInterceptorId = AxiosUtil.interceptors.response.use(
    response => {
      analyticsService.requestTimerStop((response?.config as ConfigWithId)?.id)
      return decodeHtmlResponseData(response)
    },
    error => {
      if (401 === error.response.status) {
        dispatch(setChallengeAuthenticationAction(true))
      }
      analyticsService.requestTimerStop(
        (error?.response?.config as ConfigWithId)?.id,
      )
      analyticsService.axiosException(error)
      throw error
    },
  )
}

const addAxiosInterceptorsForAngular = (dispatch: Dispatch) => {
  //If interceptors already exist, eject them to avoid duplication of interceptors
  AxiosUtil.interceptors.request.eject(angularRequestInterceptorId)
  AxiosUtil.interceptors.response.eject(angularResponseInterceptorId)
  angularRequestInterceptorId = AxiosUtil.interceptors.request.use(
    config => {
      const satellizerIdToken = localStorage.getItem('satellizer_id_token')
      const satellizerToken = localStorage.getItem('satellizer_token')

      if (satellizerToken && config.headers) {
        config.headers.Authorization = 'Bearer ' + satellizerToken
      }
      if (satellizerIdToken && config.headers) {
        config.headers.id_token = satellizerIdToken
      }
      config.params = { key: getApiKey() }
      try {
        ;(config as ConfigWithId).id = analyticsService.requestTimerStart(
          config.url!,
        )
      } catch (error) {
        console.error('Error while tracking analytics call', error)
      }
      return config
    },
    error => {
      analyticsService.axiosException(error)
      return error
    },
  )

  angularResponseInterceptorId = AxiosUtil.interceptors.response.use(
    response => {
      analyticsService.requestTimerStop((response?.config as ConfigWithId)?.id)
      return decodeHtmlResponseData(response)
    },
    error => {
      if (401 === error.response.status) {
        dispatch(setChallengeAuthenticationAction(true))
      }
      analyticsService.requestTimerStop(
        (error?.response?.config as ConfigWithId)?.id,
      )
      analyticsService.axiosException(error)
      throw error
    },
  )
}

const decodeHtmlResponseData = response => {
  if (response?.data) {
    response.data = decodeHtmlObject(response.data)
  }
  return response
}

const decodeHtmlObject = (obj, seen = new WeakSet()) => {
  if (obj === null || obj === undefined) return obj
  if (typeof obj === 'string') return decodeHtml(obj)
  if (typeof obj !== 'object') return obj
  if (seen.has(obj)) return obj
  seen.add(obj)

  if (Array.isArray(obj)) {
    return obj.map(item => decodeHtmlObject(item, seen))
  }

  for (const key of Object.keys(obj)) {
    obj[key] = decodeHtmlObject(obj[key], seen)
  }
  return obj
}

const decodeHtml = html => {
  return html
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#34;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
}

const isSSOSessionActive = () => {
  let ssoSessionActive = false
  const tokenUserName = getUserIdFromAccessToken()
  const ssoSessionCookie = Cookies.get('SSOSESSIONINFO')
  if (ssoSessionCookie && ssoSessionCookie.length > 0) {
    const ssoSessionCookieJson = JSON.parse(atob(ssoSessionCookie))
    ssoSessionActive = ssoSessionCookieJson.login_status
    const sessionCookieUserName = ssoSessionCookieJson.user
    if (tokenUserName && sessionCookieUserName) {
      ssoSessionActive =
        ssoSessionActive &&
        sessionCookieUserName.toUpperCase() === tokenUserName.toUpperCase()
    }
  }
  return ssoSessionActive
}

export const getUserIdFromAccessToken = () => {
  const accessToken =
    localStorage.getItem('access_token') ??
    localStorage.getItem('satellizer_token')
  if (accessToken) {
    const userDetails = accessToken.split('.')[1]
    if (userDetails) {
      const userDetailsJSON = JSON.parse(atob(userDetails))
      return userDetailsJSON.username
    }
  }
  return null
}

export {
  AxiosUtil,
  initAxios,
  addAxiosInterceptors,
  addAxiosInterceptorsForAngular,
}
